import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useT } from "../lib/i18n";

let deferred = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; listeners.forEach((f) => f()); });
  window.addEventListener("appinstalled", () => { deferred = null; listeners.forEach((f) => f()); });
}

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export function useInstall() {
  const [, tick] = useState(0);
  const [help, setHelp] = useState(false);
  useEffect(() => { const f = () => tick((n) => n + 1); listeners.add(f); return () => listeners.delete(f); }, []);
  const installed = isStandalone();
  const can = !installed && (!!deferred || isIOS());
  const install = async () => {
    if (deferred) { deferred.prompt(); await deferred.userChoice.catch(() => null); deferred = null; tick((n) => n + 1); }
    else setHelp(true);
  };
  return { can, installed, install, help, setHelp };
}

export function IOSHelp({ open, onOpenChange }) {
  const { t } = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0C0B10] sm:max-w-sm" data-testid="pwa-ios-help">
        <DialogHeader><DialogTitle className="font-display">{t("pwa.title")}</DialogTitle><DialogDescription>{t("pwa.ios")}</DialogDescription></DialogHeader>
        <ol className="space-y-3 text-sm text-zinc-300">
          <li className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-full bg-white/5"><Share className="h-4 w-4" /></span>{t("pwa.ios1")}</li>
          <li className="flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-full bg-white/5 font-mono text-xs">+</span>{t("pwa.ios2")}</li>
        </ol>
      </DialogContent>
    </Dialog>
  );
}

export function InstallBanner() {
  const { t } = useT();
  const { can, install, help, setHelp } = useInstall();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (localStorage.getItem("cario_install_dismissed")) return undefined;
    const h = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(h);
  }, []);
  const dismiss = () => { localStorage.setItem("cario_install_dismissed", "1"); setShow(false); };
  return (
    <>
      {show && can && (
        <div className="fixed inset-x-3 bottom-[calc(84px+env(safe-area-inset-bottom))] z-40 flex items-center gap-3 rounded-2xl border border-purple-400/25 bg-[#0C0B10]/95 p-3.5 shadow-[0_18px_50px_-12px_rgba(147,51,234,0.45)] backdrop-blur-xl animate-in slide-in-from-bottom-4 duration-300 lg:hidden" data-testid="pwa-install-banner">
          <img src="/icons/icon-192.png" alt="" className="h-11 w-11 rounded-xl ring-1 ring-white/10" />
          <div className="min-w-0 flex-1"><div className="text-sm font-medium text-white">{t("pwa.title")}</div><div className="truncate text-xs text-zinc-400">{t("pwa.body")}</div></div>
          <Button size="sm" onClick={() => { install(); if (!isIOS()) dismiss(); }} data-testid="pwa-install-button"><Download className="h-3.5 w-3.5" />{t("pwa.install")}</Button>
          <button onClick={dismiss} aria-label="Dismiss" className="grid h-9 w-9 place-items-center rounded-full text-zinc-500 hover:bg-white/5 hover:text-white" data-testid="pwa-install-dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}
      <IOSHelp open={help} onOpenChange={setHelp} />
    </>
  );
}
